import * as React from 'react';
import { useTheme } from '../../../theme';
import { useSelector } from 'react-redux';
import { getBaggageDelivery, getSegments } from '../../../Checkout/store/order/selectors';
import { getCalculatedBaggageDeliveryPrice } from '../../../Checkout/store/orderPrice/selectors';
import { useTranslation } from 'react-i18next';
import CheckoutAdditionalService from '../..';
import { useOrderService } from '../hooks';
import VipServiceSelect from '../../../VipServiceSelect';
import Modal from '../../../Modal';
import { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../utils';
import { Money } from '../../../Money';
import { useMemo } from 'react';
import { ServiceButton } from '../../types';
import ServiceError from '../ServiceError/ServiceError';
import { useIsCanModifyServices } from '../../hooks';
import { GdsServiceVipSubType, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { useGdsServiceEventToggleable } from '../../../Checkout/components/Checkout/hooks';
import { SafeHtml } from '@shared/ui';

interface Props {
	aggregationIcons?: React.ReactNode[];
	aggregationMode?: boolean;
}

const BaggageDelivery: React.FC<Props> = ({ aggregationIcons, aggregationMode }) => {
	const theme = useTheme('CheckoutAdditionalService').BaggageDelivery;

	const segments = useSelector(getSegments);
	const baggageDelivery = useSelector(getBaggageDelivery);
	const price = useSelector(getCalculatedBaggageDeliveryPrice);

	const minPrice = React.useMemo(() => {
		return baggageDelivery.reduce((minAmount, service) => {
			if (service.price?.amount < minAmount.amount) {
				return service.price;
			}

			return minAmount;
		}, baggageDelivery?.[0]?.price);
	}, [baggageDelivery]);

	const { t } = useTranslation('Checkout');

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const {
		isSelected,
		isLoading,
		selectedServices,
		getMinPrice,
		unconfirmedServices,
		onOpen,
		onClose,
		setOpened,
		onSaveHandler,
		onRemoveHandler,
		passengers,
		opened
	} = useOrderService(baggageDelivery, OrderAdditionalServiceGdsServiceServiceType.BusinessLounge);

	useGdsServiceEventToggleable({
		services: baggageDelivery,
		serviceTypes: [OrderAdditionalServiceGdsServiceServiceType.Vip],
		serviceSubTypes: [GdsServiceVipSubType.BaggageDelivery],
		onOpen
	});

	const isCanModifyServices = useIsCanModifyServices(baggageDelivery);

	const buttons: ServiceButton[] = useMemo(() => {
		if (unconfirmedServices.length) {
			return [
				{
					className: theme.button__remove,
					action: () => onRemoveHandler(unconfirmedServices),
					text: t('Remove'),
					buttonType: 'change',
					variant: 'optionEdit',
					isLoading
				}
			];
		}

		return [
			{
				action: () => setOpened(true),
				text: isCanModifyServices ? t('Change') : t('More details'),
				isLoading
			}
		];
	}, [unconfirmedServices, isLoading]);

	if (!baggageDelivery?.length) {
		return null;
	}

	const serviceName = baggageDelivery[0]?.name ?? t('Baggage delivery');

	return (
		<>
			<CheckoutAdditionalService
				header={serviceName}
				description={baggageDelivery[0].description}
				className={theme.baggageDelivery}
				priceFrom={minPrice}
				priceTotal={price}
				isSelected={isSelected}
				isExclusive={true}
				addClick={onOpen}
				descriptionClassName={theme.descriptions}
				exclusivePlateClassName={theme.exclusivePlate}
				buttons={buttons}
				addButtonText={!isCanModifyServices ? t('More details') : null}
				aggregationIcons={aggregationIcons}
				mode={aggregationMode ? 'aggregator' : 'default'}
				isLoading={isLoading}
			>
				<ServiceError isShow={!!unconfirmedServices.length}>{baggageDelivery[0].description}</ServiceError>
			</CheckoutAdditionalService>

			<Modal
				bottomDrawer={isMobile}
				open={opened}
				onClose={onClose}
				maxWidth="sm"
				classes={{
					paper: theme.popup
				}}
			>
				<VipServiceSelect
					header={serviceName}
					passengers={passengers}
					services={baggageDelivery}
					segments={segments}
					onSave={onSaveHandler}
					selectedServices={selectedServices}
					onClose={onClose}
					subheader={
						<>
							{baggageDelivery[0].vipFullDescription && (
								<SafeHtml tag="div" html={baggageDelivery[0].vipFullDescription} />
							)}

							<span>
								<Money money={getMinPrice} /> {t('per place')}
							</span>
						</>
					}
					type="counter"
					readOnly={!isCanModifyServices}
				/>
			</Modal>
		</>
	);
};

export default BaggageDelivery;
